import React, { useEffect, useState } from "react";
import { api, SESSION_EXPIRED_MESSAGE } from "../api/api";
import { useAuth } from "../context/AuthContext";
import { canonicalRole, normalizeRole, roleLabel } from "../auth/access";
// L'ECCEZIONE ADMIN dell'elenco (MUC-1 R4) si appoggia al GATE UNICO, non a un
// confronto scritto qui: `hasRole` → `canAccess` → `getActiveRoles`, che
// riconosce l'amministratore sia per `role` di primo livello sia per tessera
// ADMIN attiva, in qualunque grafia. È lo stesso aggancio, e la stessa sede,
// che usa User List (`eAmministratore`, UsersList.jsx).
import { hasRole } from "../utils/roleUtils";
// LA SEDE UNICA DEL PREDICATO «tessera + annata» e del metro dell'anno
// (cantiere «lettore unico»): chiude la coda aperta che questa pagina si era
// scritta da sé. I corpi sono quelli che stavano qui, invariati.
import { tesseraDellAnno } from "../utils/squadre";
import { annoDiLista } from "../utils/stagione";
import HomeShell from "../components/HomeShell";
// La BARRA DI STATO DELLA STAGIONE (doc 07 §8.2), componente condiviso: dice
// come sta il CLUB — i due anni, la fase, le iscrizioni. Non è il selettore
// «Season Viewed» della barra dei comandi, che dice tutt'altro (quale annata
// questa pagina sta guardando): il doc 07 §8.2 le distingue per nome, e le due
// convivono già così in User List e Team Assignment. Legge da sé la sede
// condivisa: questa pagina non le passa nulla, perché la stagione non la
// comanda — niente `override`, niente `note`.
import SeasonStatusBar from "../components/SeasonStatusBar";
// LA PASTIGLIA DI CATEGORIA (D-ADMIN2, RD-153): stesso componente e stessa
// sede del dato di Team Assignment e User List — genere + età al 31/12
// dell'annata guardata; `gender` e `dob` sono già nella lista di `GET /users`.
import { PastigliaCategoriaSeNota } from "../components/PastigliaCategoria";
import { categoriaAnagrafica } from "../utils/categoria";
import "../components/AdminTools.css";
import "./UserContextManager.css";

// Etichetta di ruolo a schermo: vocabolario e grafia sono quelli di Team
// Assignment — stessa composizione, stessa sede unica.
//   `canonicalRole` porta la grafia storica sul ruolo del modello nuovo
//   ("SuperAdmin", "Manager" → SUPERADMIN, COACH); `roleLabel` ne dà
//   l'etichetta leggibile (ROLE_LABELS, access.js).
// Le due funzioni sono ESPORTATE da auth/access.js e si riusano di là: qui
// resta la sola composizione, che in TeamAssignment.jsx è identica e
// non è esportata. Nessun vocabolario locale, nessuna mappa copiata.
//
// ⚠️ È RESA A SCHERMO, non un confronto: nulla di ciò che la pagina legge,
// filtra, spedisce o autorizza passa di qui. `canonicalRole` resta vietato
// in scrittura (access.js), e questa funzione non scrive nulla.
function etichettaRuolo(ruolo) {
  return roleLabel(canonicalRole(ruolo));
}

// Le caselle offerte dalla finestra di modifica: ESATTAMENTE i ruoli che il
// backend ammette in un context — `RUOLI_AMMESSI_NEI_CONTEXTS`
// (server.js), che rifiuta ogni altro valore con 403.
//
// ⚠️ Prima di P5 questa costante offriva MANAGER, ADMIN e SUPERADMIN: tre
// caselle su cinque erano AZIONI DESTINATE A FALLIRE (403), e PARENT — che il
// backend ammette — non era offerto affatto. Non è quindi solo una
// sottrazione: PARENT è una voce NUOVA.
//
// ⚠️ EFFETTO DA METTERE A VERBALE: senza ADMIN e SUPERADMIN questa pagina non
// offre più alcuna via per nominare quei ruoli. La sede diventa
// `PUT /users/:id`, cioè la select di EditMemberModal (decisione 9
// dell'apertura U1-F). È il motivo per cui l'ordine era vincolato: prima si
// chiude questa via, poi si apre quella — mai le due insieme, perché il
// backend vuole «una porta sola per dato» (sede: il ramo
// `if (campo === "role")` di `PUT /users/:id` in `backend/server.js`, sotto
// il commento «UNA PORTA SOLA PER DATO» — citato per contenuto e non per
// numero di riga, che slitta a ogni fetta).
const CONTEXT_TYPES = ["PLAYER", "COACH", "PARENT"];
// TEAMTD NON È UN ROLE → è un flag del COACH

// I CONTEXT AMMESSI PER RUOLO REGISTRATO (MUC-3 R1).
//
// Quali fra le caselle qui sopra si possono AGGIUNGERE a una persona, in
// funzione del suo ruolo REGISTRATO — il campo `role` di primo livello, cioè
// esattamente ciò che la colonna «Registered role» dell'elenco già mostra
// (`etichettaRuolo(u.role)`) e che questa pagina non tocca mai.
//
//   PARENT     → COACH, PLAYER
//   COACH      → PLAYER
//   PLAYER     → COACH
//   ADMIN      → COACH, PLAYER
//   SUPERADMIN → COACH, PLAYER
//
// ⚠️ PARENT NON È MAI UN CONTEXT AMMESSO, per nessun ruolo registrato: si è
// genitori per nascita — perché si hanno figli tesserati — non per aggiunta
// da questa finestra. E nessun ruolo ripropone SÉ STESSO: chi è registrato
// PLAYER è già un giocatore, e la casella Player non gli aggiunge nulla.
//
// ⚠️ GEMELLA DICHIARATA DEL BACKEND. La stessa matrice, con gli stessi nomi,
// vive in `backend/server.js` accanto a `ruoloCanonico`, dove RIFIUTA con 400
// ciò che qui è oscurato. Non è una sede doppia per distrazione: server.js e
// frontend/src non condividono moduli — è lo stesso motivo per cui esistono
// `ruoloCanonico` e `canonicalRole`, e per cui questa pagina ricopia
// `RUOLI_AMMESSI_NEI_CONTEXTS` in `CONTEXT_TYPES`. La sede che DECIDE è il
// backend; questa è la metà che si vede. Chi cambia una riga qui cambia anche
// la riga là.
//
// ⚠️ OSCURARE NON È TOGLIERE. La matrice dice che cosa si può AGGIUNGERE:
// una tessera che la persona GIÀ POSSIEDE fuori matrice (un dato storico, o
// la tessera del suo stesso ruolo registrato) resta spuntata e si conserva —
// vedi la resa delle caselle nella finestra.
const CONTEXT_AMMESSI_PER_RUOLO = {
  PARENT: ["COACH", "PLAYER"],
  COACH: ["PLAYER"],
  PLAYER: ["COACH"],
  ADMIN: ["COACH", "PLAYER"],
  SUPERADMIN: ["COACH", "PLAYER"]
};

// PURA: dal `role` grezzo di una persona (qualunque grafia) all'elenco dei
// ruoli canonici che le si possono aggiungere come context.
//
// ⚠️ È UNA DOMANDA DI RESA, non una scrittura: `canonicalRole` si usa qui per
// CONFRONTARE il ruolo registrato con la matrice — mai per riscrivere il
// `role` di qualcuno, che questa pagina non spedisce affatto.
//
// ⚠️ VERSO SICURO: un ruolo fuori dai cinque (le grafie storiche le traduce
// già `canonicalRole`; qui cadono FAN, ESPORT, vuoto, assente) torna elenco
// VUOTO — nessuna casella aggiungibile, nessun permesso inventato. È lo
// stesso verso del backend, che a quel punto rifiuterebbe comunque.
// ⚠️ E TORNA UNA COPIA, come la gemella del backend: la riga della matrice
// non esce mai da qui, così nessun chiamante può allargarla mutandola.
function contextAmmessi(ruoloRegistrato) {
  const ammessi = CONTEXT_AMMESSI_PER_RUOLO[canonicalRole(ruoloRegistrato)];
  return Array.isArray(ammessi) ? ammessi.slice() : [];
}

/* ===============================
   LE TESSERE CHE QUESTA PAGINA LEGGE (MUC-1 R4)
=============================== */
// ⚠️ LA CODA APERTA È CHIUSA. Il commento che stava qui diceva: «È IL
// PREDICATO DI USER LIST, REPLICATO E NON REINVENTATO… qui si ricopiano alla
// lettera invece di estrarle in una sede condivisa, perché quell'estrazione
// toccherebbe una pagina che questa fetta non ha il permesso di modificare.
// LA SEDE UNICA DEL PREDICATO “tessera + annata” RESTA UNA CODA APERTA: oggi
// le copie sono tre (User List, Team Assignment, questa), e la terza va
// tenuta allineata a mano».
//
// La sede unica ora esiste — `utils/squadre.js`, `utils/stagione.js` — e le
// copie non vanno più tenute allineate a mano: `annoDiUcm` e
// `tesseraValidaNellAnno` non vivono più qui, e i loro corpi sono quelli di
// questa pagina, invariati. `normalizza` di `roleUtils` È `normalizeRole` di
// `access` (stesso simbolo, ri-esportato): il modulo usa il primo nome, e
// non è un cambio di metro.
//
// ERA: l'elenco si filtrava sullo status della PERSONA (`status === "ACTIVE"`),
// e la pagina non sapeva nulla di annate. ORA: compare chi ha almeno una
// tessera ACTIVE dell'anno GUARDATO, più — sempre — gli amministratori.
//
// Le due cautele del predicato restano, e ora vivono nel modulo:
//
// ⚠️ `cardSeason` MANCA, non vale `null`: la proiezione di lista lo emette con
// uno spread condizionale, quindi una tessera che non ce l'ha arriva SENZA LA
// CHIAVE. Si verifica perciò la PRESENZA della chiave, come in User List. È
// l'opzione predefinita `senzaAnno: "escludi"`.
//
// ⚠️ ANNATA IGNOTA (`anno === null`) → la regola dell'anno NON si applica, e
// restano le sole ACTIVE. È la stessa dottrina di User List e del cancello
// dell'anno in AuthContext: su un dato che non si sa leggere non si inventa
// nulla, e qui inventare vorrebbe dire svuotare la pagina che amministra i
// ruoli proprio quando la stagione non si è letta.
const haTessereNellAnno = (user, anno) =>
  Array.isArray(user?.contexts)
    ? user.contexts.some((c) => tesseraDellAnno(c, anno))
    : false;

// ⚠️ L'ECCEZIONE ADMIN NON PUÒ POGGIARE SULL'ANNO, ed è un fatto del dato: le
// tessere amministrative non portano `cardSeason` e non possono portarlo.
// Senza questo OPPURE, il primo effetto della regola nuova sarebbe far sparire
// gli amministratori dalla pagina che li amministra.
const eAmministratore = (user) => hasRole(user, "ADMIN", "SUPERADMIN");

// Il messaggio che il backend ha messo nel corpo della risposta, quando c'è.
//
// ⚠️ Si estrae dalla STRINGA dell'errore: `apiRequest` lancia
// `new Error("API <path> failed: <status> <corpo grezzo>")` (api.js), col
// corpo concatenato in fondo. Da 90e435a `api.js` allega anche `err.status` e
// `err.body` (`erroreDiRisposta`): questa funzione non è ancora passata a
// leggerli — igiene rinviata, non un limite di `api.js`.
//
// Difensiva per costruzione: senza graffa, o con un corpo che non è JSON, o se
// un domani `api.js` cambiasse formato, restituisce "" e il chiamante ricade
// sul messaggio generico. Non lancia mai.
//
// ⚠️ Il rimedio vero sta in `api.js` — allegare stato e corpo all'errore, una
// volta per tutta l'app — ma quella è una sede condivisa da OGNI chiamata:
// fuori dal perimetro di P5, segnalata come punto futuro. Lo stesso limite
// acceca oggi anche EditMemberModal, che sul 403 «una porta sola per dato»
// mostra un generico «Failed to update» invece del `detail` che direbbe dove
// quel ruolo si cambia davvero.
//
// ⚠️ E questo non è il primo lettore di quella stringa: `TeamAssignment.jsx`
// (`codiceHttp`/`messaggioErrore`) ne estrae lo STATO come qui se ne estrae il
// CORPO. Non è un doppione — leggono due cose diverse — ma il giorno in cui
// `api.js` verrà sanato i punti da cambiare sono DUE, e vanno cambiati
// insieme.
function dettaglioDalBackend(err) {
  const testo = String((err && err.message) || "");
  const inizio = testo.indexOf("{");

  if (inizio === -1) return "";

  try {
    const corpo = JSON.parse(testo.slice(inizio));
    return String((corpo && (corpo.detail || corpo.error)) || "");
  } catch {
    return "";
  }
}

// ⚠️ Non afferma una causa, di proposito. Sotto lo stesso rifiuto stanno due
// 403 diversi — ruolo non ammesso (server.js, CON `detail`) e
// autorità pari o superiore sul bersaglio (server.js, SENZA) — più i
// 400 e i 404.
//
// ⚠️ «not changed» è vero nei rifiuti decisi PRIMA della scrittura, che sono il
// caso comune: il backend supera ogni controllo prima di toccare Firestore.
// NON è una garanzia, e va detto: un 500 può nascere dalla scrittura stessa
// (il `catch` della rotta avvolge anche `userRef.set`), e una risposta persa
// per un guasto di rete lascerebbe un salvataggio riuscito raccontato come
// fallito. In quei casi nessun messaggio sarebbe certo, e invitare a riprovare
// resta la mossa giusta — anche perché il salvataggio è idempotente: da F3c la
// rotta non riscrive più il mazzo, conserva le tessere che restano e ripetere
// lo stesso Save lascia il documento identico (V2). Stessa cautela, e stessa
// formulazione, già ratificate per EditMemberModal (U1-S coda/1).
const SALVATAGGIO_FALLITO =
  "Save failed: the contexts were not changed. Please try again.";

// R9.4: il 403 d'AUTORITÀ sul bersaglio (un ADMIN su sé stesso o su un altro
// ADMIN, un SUPERADMIN su un altro SUPERADMIN) non è un guasto da riprovare: è
// un permesso negato, e lo si dice.
const AUTORITA_NEGATA =
  "You can't change the contexts of another administrator.";

// Riconosce QUEL 403 dal corpo: è l'unico 403 della catena di questa rotta
// (requireAuth → requireRole → PUT /admin/users/:id/contexts) che risponde
// `{ error: "Forbidden" }` SENZA `detail`. Gli altri — ruolo non ammesso nei
// contesti, utente non in anagrafica, account non attivo, ruolo del chiamante
// insufficiente — portano tutti un `detail` e restano al ramo di prima.
// Difensiva: un corpo assente o non JSON (un 403 di un intermediario) non è
// riconosciuto e ricade anch'esso nel ramo di prima. Non lancia mai.
function eRifiutoDiAutorita(err) {
  if (!err || err.status !== 403) return false;

  try {
    const corpo = JSON.parse(String(err.body || ""));
    return Boolean(corpo && corpo.error === "Forbidden" && !corpo.detail);
  } catch {
    return false;
  }
}

export default function UserContextManager() {
  // ⚠️ I DUE ANNI E LO STATO DELLA LETTURA arrivano dalla sede condivisa
  // (AuthContext, YS-5), come in User List e Team Assignment: questa pagina non
  // legge la cassettina per conto suo e non comanda la stagione.
  const {
    user,
    refreshUserFromBackend,
    operatingSeason,
    enrollmentSeason,
    seasonStatus
  } = useAuth();

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedContexts, setSelectedContexts] = useState([]);

  // ⚠️ QUI C'ERA LO STATO DELLA SPUNTA «All teams» (prima ancora: `teamTD`).
  // È caduto con la casella, in F3c: questa finestra dichiara i RUOLI, le
  // squadre le dichiara Team Assignment. Finché lo stato è esistito, il Save
  // di una finestra che parla di ruoli riscriveva anche le squadre — e le
  // toglieva, o le allargava a tutte.
  //
  // UN SOLO stato per ciò che la finestra ha da dire, come deciso dal
  // capomastro («riusando lo stato errore»): `{ tipo, testo }`, con `tipo`
  // "errore" oppure "avviso". Il tipo serve perché l'avviso di conversione NON
  // è un guasto — dipingerlo come un errore farebbe credere a un salvataggio
  // fallito che non è nemmeno partito. Due stati separati avrebbero potuto
  // mostrarsi insieme e contraddirsi.
  const [messaggio, setMessaggio] = useState(null);

  // LA CONFERMA DI PAGINA (D-ADMIN2, RD-153): è un secondo stato, e non un
  // terzo tipo di `messaggio`, perché vive in un altro posto. `messaggio` sta
  // DENTRO la finestra, che al successo si chiude — una conferma messa lì
  // sparirebbe con lei. Questa sta sulla pagina e resta dopo la chiusura.
  // Stessa forma `{ tipo, testo }` del modello (SystemView, `esito`); le due
  // non si contraddicono, perché si svuota riaprendo la finestra.
  const [esitoPagina, setEsitoPagina] = useState(null);

  // L'annata scelta a mano nel selettore «Season Viewed». `undefined` = non ha
  // ancora scelto: vedi `stagionePredefinita`, poco sotto.
  const [stagioneScelta, setStagioneScelta] = useState(undefined);

  /* =========================
     L'ANNO GUARDATO (MUC-1 R1/R5/R7)
  ========================= */
  // L'annata su cui questa pagina opera: decide CHI compare nell'elenco (R4) e
  // su quale anno il Salva scrive (R1). Le due cose si muovono insieme perché
  // leggono la stessa variabile.
  //
  // Il DEFAULT — si calcola, non si assegna, come in User List: `undefined`
  // vuol dire «non ha ancora scelto», e finché è così vale il predefinito.
  // Assegnarlo per effetto obbligherebbe poi a difenderlo da se stesso, e ogni
  // difesa è un punto in cui la scelta dell'amministratore può essere
  // sovrascritta alle sue spalle.
  //
  // ⚠️ IL PREDEFINITO È L'OPERATIVO (R5), e non l'iscrizione: questa pagina
  // amministra i ruoli dei soci del club com'è OGGI, ed è l'anno su cui lo
  // specchio giudica il «dentro o fuori». Chi deve lavorare sull'annata nuova
  // si sposta con il selettore.
  //
  // ⚠️ SERVONO ENTRAMBI GLI ANNI, non il solo operativo, ed è l'allineamento
  // esatto a ciò che la porta pretende: la rotta rifiuta con 409 se **uno solo**
  // dei due non si legge, perché senza la coppia non ha il metro con cui
  // validare l'anno guardato. La sede condivisa, invece, dichiara la stagione
  // «ready» anche con un anno solo. Chiedendo qui la stessa coppia, la pagina
  // non si mostra mai operativa mentre ogni Save prenderebbe 409: o è in sola
  // lettura per intero, o funziona per intero.
  //
  // ⚠️ Sta PRIMA delle voci del selettore perché quelle lo leggono: l'ordine
  // delle dichiarazioni qui non è estetico, invertirlo è un errore in
  // esecuzione (la `const` non esiste prima della sua riga).
  const stagionePredefinita =
    seasonStatus === "ready" &&
    operatingSeason != null &&
    enrollmentSeason != null
      ? operatingSeason
      : null;

  // ⚠️ LE DUE SOLE POSIZIONI sono gli anni che il CLUB dichiara: l'operativo e
  // quello d'iscrizione. Non le annate dei dati (User List le offre, perché è
  // un archivio da consultare): qui si SCRIVE, e il backend accetta soltanto
  // quei due — un terzo anno prenderebbe 400. Il selettore offre quindi
  // esattamente ciò che la porta ammette. In stagione unica i due numeri
  // coincidono e il `Set` lascia una voce sola.
  //
  // ⚠️ E NON SI OFFRE NULLA SE LA PAGINA È IN DEGRADO. La sede condivisa
  // dichiara `seasonStatus: "ready"` anche quando UNO SOLO dei due anni si è
  // letto: con l'operativo illeggibile e l'iscrizione buona, il selettore
  // avrebbe mostrato quell'unica voce come se fosse selezionata, mentre
  // `annoGuardato` vale `null` e il Save rifiuta. Un'etichetta che dice il
  // falso, e nemmeno cliccabile (scegliere l'opzione già mostrata non emette
  // `change`). Senza il predefinito la pagina è in sola lettura, e il campo
  // resta vuoto perché non c'è davvero nessuna annata da offrire.
  const stagioniDisponibili =
    stagionePredefinita === null
      ? []
      : Array.from(
          [operatingSeason, enrollmentSeason].reduce(
            (elenco, anno) => (anno == null ? elenco : elenco.add(anno)),
            new Set()
          )
        ).sort((a, b) => b - a);

  // ⚠️ UNA SCELTA CHE NON È PIÙ FRA LE DUE non si onora: se la cassettina
  // cambia sotto i piedi mentre la pagina è aperta, si torna al predefinito
  // invece di mandare al server un anno che prenderebbe 400.
  const annoGuardato =
    stagioneScelta !== undefined && stagioniDisponibili.includes(stagioneScelta)
      ? stagioneScelta
      : stagionePredefinita;

  // ⚠️ IL DEGRADO, dichiarato. Se la stagione non si è letta
  // (`seasonStatus !== "ready"`) o l'anno non c'è, `annoGuardato` vale `null` e
  // la pagina NON si svuota: `tesseraDellAnno` sospende la regola
  // dell'anno e restano le tessere ACTIVE, cioè l'elenco di prima di questa
  // fetta. Il SALVA, invece, si ferma (vedi `saveContexts`): mostrare è
  // innocuo, scrivere su un anno che non si conosce no — e il backend
  // rifiuterebbe comunque, perché senza cassettina non c'è annata da validare.

  /* =========================
     LOAD USERS
  ========================= */
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    const usersData = await api.get("/users");
    const list = Array.isArray(usersData) ? usersData : [];

    // ⚠️ QUI NON SI FILTRA PIÙ (MUC-1 R4). C'era
    // `normalizeRole(u.status) === "ACTIVE"`, cioè lo status della PERSONA: un
    // filtro cieco all'annata, che mostrava chi il club considera dentro oggi
    // qualunque anno l'amministratore stesse amministrando. Il vaglio si è
    // spostato dove può leggere l'anno guardato — la coorte d'annata, poco più
    // sotto — e per la stessa ragione per cui in User List sta a monte delle
    // due basi: applicato in un punto solo, elenco e ricerca parlano per
    // costruzione della stessa annata.
    //
    // La lista integrale resta in `users` perché la coorte si ricalcola quando
    // l'anno guardato cambia, senza rileggere `GET /users`.
    setUsers(list);
  };

  /* =========================
     FILTERS
  ========================= */
  useEffect(() => {
    // ⚠️ LA COORTE D'ANNATA (MUC-1 R4), a monte dei filtri di ricerca.
    // Compare chi ha almeno una tessera ACTIVE dell'anno guardato, OPPURE chi è
    // amministratore — un OPPURE, non un filtro in più. È il criterio di User
    // List, e da qui in avanti le due pagine mostrano le stesse persone quando
    // guardano la stessa annata.
    //
    // ⚠️ EFFETTO DA METTERE A VERBALE, e non è un dettaglio: chi ha SOLO
    // tessere senza `cardSeason` (i soci non ancora migrati) esce dall'elenco,
    // perché il predicato esige la presenza della chiave. Prima compariva, per
    // il solo fatto di essere ACTIVE. È lo stesso effetto che User List e Team
    // Assignment hanno già su quelle persone; qui pesa di più, perché questa è
    // la pagina che i ruoli li ripara. Vale finché la migrazione (YS-N) non
    // riempie l'anno, e in beta i dati nascono già con l'annata. Segnalato al
    // committente nel referto, insieme al vicolo cieco degli ARCHIVED.
    let data = users.filter(
      (u) => haTessereNellAnno(u, annoGuardato) || eAmministratore(u)
    );

    if (search) {
      data = data.filter((u) =>
        `${u.firstName} ${u.lastName}`
          .toLowerCase()
          .includes(search.toLowerCase())
      );
    }

    if (roleFilter !== "ALL") {
      data = data.filter(
        (u) => u.role?.toLowerCase() === roleFilter.toLowerCase()
      );
    }

    setFilteredUsers(data);
    // `annoGuardato` entra fra le dipendenze: cambiando annata l'elenco si
    // rifà, senza rileggere `GET /users`. (Oggi cambia solo quando la stagione
    // finisce di caricarsi; dalla TAPPA B lo muoverà il selettore.)
  }, [search, roleFilter, users, annoGuardato]);

  /* =========================
     CONTEXT EDIT
  ========================= */
  const openEditor = (u) => {
    setSelectedUser(u);
    setMessaggio(null);
    setEsitoPagina(null);

    // ⚠️ IL RESIDUO È CHIUSO (F9b). Qui il filtro sullo status era CRUDO
    // (`c.status === "ACTIVE"`, confronto letterale) e il commento di P5
    // dichiarava il difetto che ne discendeva: un context salvato
    // `status: "Active"` non entrava in questa lettura, quindi non accendeva
    // alcuna casella — mentre il backend, che lo status lo normalizza, lo
    // vedeva attivo a tutti gli effetti. Se il suo ruolo non risultava fra
    // quelli spuntati, il Save lo archiviava: non perché riscriva tutto (da
    // F3c non lo fa più), ma perché per il backend era una tessera membro
    // attiva di un ruolo non più chiesto. La guardia sul payload vuoto in
    // `saveContexts` intercettava il caso più comune (nessun context
    // leggibile → nessuna spunta), ma NON quello in cui l'amministratore ne
    // spuntava uno e perdeva gli altri.
    //
    // Con `normalizeRole` — sola grafia, nessun vocabolario — questa pagina
    // legge lo status come lo legge il backend, e la finestra mostra le
    // tessere che il backend considera attive. Nessuna tessera in più: il
    // normalizzatore non traduce ARCHIVED o PENDING in ACTIVE, si limita a
    // togliere spazi e maiuscole.
    //
    // ⚠️ E LE PENDING ACCENDONO LA CASELLA (YS-3b). Fin qui la finestra
    // leggeva le sole tessere ACTIVE, e una tessera PENDING risultava
    // INVISIBILE a chi salva: la casella del suo ruolo restava spenta, e
    // l'amministratore — davanti a un Save che sembrava non aver funzionato —
    // la ri-spuntava, facendo nascere una tessera gemella. Il difetto vero sta
    // nel backend, ed è lì che si chiude (l'unicità del ruolo ora vale su
    // tutte le tessere non archiviate); questa pagina smette comunque di
    // INDURRE il gesto, perché mostra ciò che la persona ha davvero.
    //
    // ⚠️ Non concede nulla: qui si decide quali CASELLE si accendono in una
    // finestra degli amministratori (ADMIN e SUPERADMIN), non chi entra e non
    // cosa vale. Chi entra lo dice il gate della pagina (`isAdmin`, più
    // sotto); i lettori del backend continuano a scartare ogni tessera che
    // non sia ACTIVE.
    // ⚠️ E SOLO LE TESSERE DELL'ANNATA GUARDATA (MUC-1). Questa riga è la
    // metà frontend della regola nuova, e senza di lei la fetta si morde la
    // coda: il backend ora governa le tessere «dell'anno guardato, più quelle
    // mute» e lascia INTATTE le altre, mentre la finestra continuava ad
    // accendere una casella per OGNI tessera non archiviata, di qualunque
    // annata. Il danno era il gesto più banale della pagina, «Save senza
    // toccare nulla»: una persona con `PLAYER ACTIVE 2026` e `COACH PENDING
    // 2027`, guardata nel 2026, mandava anche COACH fra i ruoli chiesti — e
    // siccome la tessera 2027 non è governata, il ramo «crea» del backend ne
    // faceva NASCERE una nuova, `COACH ACTIVE 2026`. Un ruolo dell'anno
    // prossimo diventava un ruolo di oggi senza che nessuno l'avesse chiesto.
    //
    // ⚠️ IL PREDICATO È QUELLO DEL BACKEND, non quello dell'elenco: si tiene
    // l'annata guardata E la tessera MUTA (`anno === null`), perché è
    // esattamente l'insieme che la rotta governa. Se la finestra dichiarasse
    // meno, un Save archivierebbe tessere che l'amministratore vede spente
    // senza saperlo; se dichiarasse di più, ne farebbe nascere.
    //
    // ⚠️ E CON LA STAGIONE NON LETTA (`annoGuardato === null`) non si filtra
    // affatto: la regola dell'anno si sospende, come nell'elenco. Il Save è
    // comunque bloccato a monte, quindi la finestra resta di sola lettura.
    //
    // Conseguenza da conoscere: dalla vista 2026 non si toglie un ruolo che
    // esiste solo nel 2027 — per agire su quella tessera si cambia «Season
    // Viewed». È R3 alla lettera: l'annata che non si guarda non si tocca.
    //
    // ⚠️ DUE CONTROLLI DIVENTANO UNA CHIAMATA SOLA, e non cambia nulla: il
    // filtro di stato e la chiusura d'annata erano una congiunzione, e
    // `tesseraDellAnno` la esprime con i suoi due parametri. Le due
    // differenze di questa pagina, che nessun'altra ha, sono ora SCRITTE
    // invece che ricopiate: `stati: ["ACTIVE","PENDING"]` (non il solo
    // ACTIVE) e `senzaAnno: "includi"` (la tessera MUTA passa, dove l'elenco
    // dei soci la esclude).
    const dellAnnataGuardata = (c) =>
      tesseraDellAnno(c, annoGuardato, {
        stati: ["ACTIVE", "PENDING"],
        senzaAnno: "includi"
      });

    const governate = Array.isArray(u.contexts)
      ? u.contexts.filter(dellAnnataGuardata)
      : [];

    // ⚠️ DEROGA DICHIARATA AL «DIVIETO D'USO IN SCRITTURA» di `canonicalRole`
    // (il blocco così intitolato in access.js), autorizzata dal capomastro il
    // 10/8/2026 (P5, decisione 1, opzione C). Da F3c la deroga è di fatto
    // RIENTRATA, e va detto perché: la casella pre-spuntata è ancora ciò che
    // si salva — il payload nomina il ruolo canonico — ma il backend abbina
    // per ruolo canonico e CONSERVA la tessera storica com'è, grafia compresa
    // («Manager» resta «Manager» nei dati). La grafia canonica si scrive solo
    // su una tessera NUOVA, che nascerebbe comunque nel modello nuovo. Resta
    // dunque una traduzione di sola LETTURA, più il nome del ruolo chiesto.
    //
    // Perché è ammesso proprio qui:
    //   1. la conversione è IMPOSTA DAL BACKEND, non scelta da noi: la rotta
    //      ammette solo PLAYER/COACH/PARENT (server.js) e rifiuta
    //      "MANAGER" con 403. Non c'è modo di CHIEDERE quel ruolo nella grafia
    //      storica: senza traduzione la pagina non è «più prudente», è
    //      semplicemente INUTILIZZABILE su quei documenti (403 a ogni Save);
    //   2. non la decide il codice ma un AMMINISTRATORE, premendo Save;
    //   3. non è silenziosa: `tradotti` la dichiara a schermo prima del Save.
    // Il divieto di access.js esiste contro le conversioni silenziose decise
    // dal codice. Nessuna delle tre condizioni vale altrove: questa deroga
    // NON crea un precedente.
    //
    // Senza la traduzione, il difetto era: un context "Manager" (o "Coach", per
    // la sola grafia) non corrispondeva ad alcuna casella e l'amministratore
    // vedeva la finestra VUOTA mentre la colonna «Contexts» dell'elenco gli
    // mostrava i contesti di quella persona.
    const roles = [];
    const tradotti = [];
    const nonGestiti = [];

    governate.forEach((c) => {
      const canonico = canonicalRole(c.role);

      // ⚠️ Ciò che le caselle non sanno rappresentare NON entra nello stato.
      // Senza questo filtro un context ADMIN, SUPERADMIN o fuori vocabolario
      // (FAN, ESPORT) restava nello stato SENZA una casella che lo mostrasse,
      // e `saveContexts` lo spediva lo stesso: 403 a ogni tentativo
      // (server.js), per sempre, senza alcun gesto capace di
      // toglierlo (decisione del capomastro, U1 semplice).
      //
      // ⚠️ DA F3c il filtro non è più anche una VIA D'USCITA, e va messo a
      // verbale: la rotta lascia INTATTE le tessere che non governa (ADMIN,
      // SUPERADMIN, fuori vocabolario, senza ruolo), quindi salvare da qui non
      // le archivia più. Il filtro serve ancora — tiene fuori dal payload ciò
      // che il backend rifiuterebbe — ma questa pagina non offre alcun gesto
      // per togliere un context spurio: l'avviso a schermo lo dice («saving
      // leaves it unchanged») e la sede della pulizia resta da decidere.
      //
      // ⚠️ FATTO A VERBALE, confermato dal proprietario del club (10/8/2026),
      // perché non riemerga il falso allarme «strappo di privilegio»: nel
      // modello, e nei dati di produzione, l'essere ADMIN o SUPERADMIN dipende
      // SEMPRE dal `role` di base e MAI da un context. Archiviare un context
      // ADMIN sarebbe quindi la pulizia di un dato non valido, non la
      // sottrazione di un potere — e da F3c non accade più nemmeno quella: la
      // tessera resta com'è. Il `role` di primo livello questa pagina non lo tocca
      // affatto (si cambia da PUT /users/:id). In uso valido il filtro non ha
      // alcun effetto, perché i contexts sono già solo PLAYER/COACH/PARENT.
      // ⚠️ Ci cade dentro anche il context SENZA ruolo (assente, vuoto, non
      // stringa): `canonico` è "" e nessuna casella lo rappresenta. Prima
      // usciva in silenzio da qui, e il Save lo archiviava senza che nulla
      // l'avesse annunciato — mentre può portare `team` e `approvedTeams`, che
      // `Messaging.jsx` legge SENZA guardare il ruolo. Ora è dichiarato come
      // gli altri, con l'etichetta «(no role)» perché una stringa vuota a
      // schermo non si vedrebbe.
      if (!CONTEXT_TYPES.includes(canonico)) {
        const voce = canonico ? String(c.role) : "(no role)";
        if (!nonGestiti.includes(voce)) nonGestiti.push(voce);
        return;
      }

      // Senza il controllo dei doppioni, due context storici della stessa
      // persona — "Manager" e "TD", che il vocabolario manda entrambi su
      // COACH — spedirebbero DUE context COACH identici.
      if (!roles.includes(canonico)) roles.push(canonico);

      // Si annota solo la traduzione di VOCABOLARIO, non quella di grafia:
      // "Coach" → "COACH" non è un cambiamento imputabile a questa pagina,
      // perché il backend scrive comunque il ruolo normalizzato
      // (server.js), qualunque cosa gli si mandi.
      if (normalizeRole(c.role) !== canonico) {
        const voce = `${c.role} → ${canonico}`;
        if (!tradotti.includes(voce)) tradotti.push(voce);
      }
    });

    setSelectedContexts(roles);

    // Un solo pannello per le cose che la finestra può avere da dire: una
    // persona può insieme portare un ruolo storico da tradurre e un context che
    // questa pagina non gestisce.
    const avvisi = [];

    // ⚠️ LA FINESTRA MUTA (MUC-1, rilievo del verificatore). Da quando le
    // caselle si accendono per la sola annata guardata, può capitare che una
    // persona abbia ruoli — ma tutti in un'ALTRA annata: la finestra si apre
    // senza nulla di acceso, e premendo Save si otterrebbe «Select at least one
    // context», che è vero ma non spiega il motivo. Il caso si raggiunge per la
    // via dell'eccezione admin (chi non è amministratore e non ha tessere
    // dell'annata non compare nemmeno in elenco).
    //
    // Non è un errore: è la regola R3 detta a schermo. Chi vuole agire su
    // quelle tessere cambia «Season Viewed» — e ora la finestra glielo dice.
    const altreAnnate = Array.isArray(u.contexts)
      ? Array.from(
          u.contexts.reduce((elenco, c) => {
            const stato = normalizeRole(c?.status);
            if (stato !== "ACTIVE" && stato !== "PENDING") return elenco;
            if (!CONTEXT_TYPES.includes(canonicalRole(c?.role))) return elenco;
            if (dellAnnataGuardata(c)) return elenco;
            const anno = annoDiLista(c?.cardSeason);
            return anno === null ? elenco : elenco.add(anno);
          }, new Set())
        ).sort((a, b) => a - b)
      : [];

    if (roles.length === 0 && altreAnnate.length > 0) {
      avvisi.push(
        `This person has no roles in the season you are viewing ` +
        `(${annoGuardato}). Their roles belong to ${altreAnnate.join(", ")}: ` +
        `switch "Season Viewed" to manage them.`
      );
    }

    if (tradotti.length > 0) {
      avvisi.push(
        `Historic roles in this person's contexts (${tradotti.join(", ")}). ` +
        `They are shown here under the new spelling; saving keeps the existing ` +
        `contexts and does not rewrite their spelling.`
      );
    }

    if (nonGestiti.length > 0) {
      const uno = nonGestiti.length === 1;
      avvisi.push(
        `This person has ${uno ? "a context" : "contexts"} this page does not ` +
        `manage (${nonGestiti.join(", ")}): saving leaves ` +
        `${uno ? "it" : "them"} unchanged.`
      );
    }

    if (avvisi.length > 0) {
      setMessaggio({ tipo: "avviso", testo: avvisi.join(" ") });
    }

    // ⚠️ QUI SI LEGGEVA LO STATO INIZIALE DELLA SPUNTA «All teams», dalla
    // vecchia spia `team === "TEAMTD"`. È caduta con la casella (F3c), e vale
    // la pena dire perché era proprio quella lettura a fare danno: la spia non
    // distingue «tutte le squadre» da «un elenco vero», e Team Assignment
    // scrive elenchi veri (`team:"TEAMTD"`, `approvedTeams:["U12"]`). Su un
    // coach con tre squadre la casella si accendeva DA SOLA, e il Save
    // successivo lo allargava a tutte le squadre del club — senza che nessuno
    // l'avesse chiesto e senza che nulla lo dicesse a schermo.
    //
    // Ora questa finestra non legge, non mostra e non salva squadre: le
    // squadre di una tessera si vedono e si cambiano in Team Assignment.
  };

  const toggleContext = (role) => {
    setSelectedContexts((prev) => {
      if (prev.includes(role)) {
        return prev.length === 1 ? prev : prev.filter((r) => r !== role);
      }
      return [...prev, role];
    });
  };

  /* =========================
     SAVE CONTEXTS
  ========================= */
  const saveContexts = async () => {
    if (!selectedUser) {
      return;
    }

    setMessaggio(null);

    // ⚠️ GUARDIA SUL PAYLOAD VUOTO. Un array vuoto il backend lo ACCETTA
    // (`Array.isArray([])` è vero, nessun ruolo non ammesso) e il suo effetto è
    // di archiviare TUTTE le tessere membro attive — PLAYER, COACH, PARENT —
    // senza sostituirne nessuna (le altre, da F3c, restano intatte): una
    // cancellazione, ottenuta premendo Save su una finestra che sembra vuota.
    //
    // Non toglie alcuna funzione, perché l'array vuoto NON è raggiungibile per
    // volontà di chi amministra: `toggleContext` impedisce di despuntare
    // l'ultima casella. Ci si arriva solo quando la LETTURA non ha trovato
    // nulla — e allora un messaggio è la risposta giusta, non una scrittura.
    //
    // ⚠️ Conseguenza da conoscere, per chi ha SOLO contexts non gestiti (un
    // ADMIN sporco e nient'altro): la finestra avverte che quel context resta
    // com'è, e la guardia chiede comunque di spuntare almeno un contesto
    // gestito — da qui non c'è nulla da salvare su quella persona finché non
    // le si dà un ruolo. È voluto: l'alternativa sarebbe spedire un
    // array vuoto, cioè la cancellazione che questa guardia esiste per
    // impedire — e distinguere «vuoto perché non c'era nulla da leggere» da
    // «vuoto perché ho scartato qualcosa» aprirebbe la porta che si sta
    // chiudendo.
    if (selectedContexts.length === 0) {
      setMessaggio({
        tipo: "errore",
        testo: "Select at least one context, or press Cancel."
      });
      return;
    }

    // ⚠️ SENZA ANNO NON SI SCRIVE (MUC-1 R1/R5). Il Salva timbra la tessera
    // nuova con l'anno guardato: se la stagione non si è letta, quell'anno non
    // c'è, e non lo si inventa. Il rifiuto è QUI e non solo a schermo perché
    // dica una cosa comprensibile — il backend risponderebbe comunque 409, ma
    // senza spiegare che il guasto è la lettura della stagione.
    //
    // ⚠️ Mostrare senza annata resta lecito (l'elenco degrada alle sole ACTIVE):
    // è scrivere che non lo è.
    if (annoGuardato === null) {
      setMessaggio({
        tipo: "errore",
        testo:
          "Could not read the club season. Please try again in a moment."
      });
      return;
    }

    // ⚠️ IL REPERTO È CHIUSO (F3c), e vale la pena dire cosa era: il
    // salvataggio RISCRIVEVA le squadre di ogni tessera a valori fissi,
    // qualunque cosa il context contenesse — una squadra vera (`team:"U12"`)
    // andava persa, un elenco PARZIALE veniva ALLARGATO a tutte le squadre —
    // perché il backend archiviava tutto e ricreava da zero. Da F3c questa
    // finestra dichiara i soli RUOLI: le tessere che restano se le tiene
    // com'erano, squadre e codicino compresi.
    //
    // 🔹 IL PAYLOAD DICE SOLO RUOLI. Niente `allTeams`, niente `teams`,
    // niente `team`/`approvedTeams`: le squadre non sono materia di questa
    // porta, e mandarle — anche solo a valori fissi — sarebbe rimetterle in
    // mano a chi non le governa. La sede è Team Assignment, che le scrive per
    // tessera (PUT /users/:userId/contexts/:tesseraId/teams).
    //
    // 🔹 NESSUN ID DI TESSERA nel payload: l'abbinamento fra ruolo chiesto e
    // tessera esistente lo fa il BACKEND, per ruolo canonico. È lui che ha in
    // mano il documento vero, e non deve credere al client su quale tessera
    // conservare.
    try {
      const payload = selectedContexts.map((role) => ({
        contextId: "QPSC",
        contextType: "CLUB",
        role,
        status: "ACTIVE"
      }));

      // 🔹 L'ANNO GUARDATO VIAGGIA NEL CORPO (MUC-1 R1), nel campo `season`:
      // stesso nome e stesso metro delle due sorelle di gesto admin — l'Approve
      // di `PUT /users/:id/status` e la rotta delle squadre — che l'annata la
      // dichiarano già così. È il dato del GESTO: quale anno l'amministratore
      // stia amministrando lo sa solo questa pagina.
      //
      // ⚠️ Il server non si fida e lo VALIDA contro i due anni della cassettina
      // (400 se è un altro anno, 409 se la cassettina non si legge): questo
      // campo dichiara l'intenzione, non la autorizza.
      await api.put(
        `/admin/users/${selectedUser.id}/contexts`,
        { contexts: payload, season: annoGuardato }
      );

      setSelectedUser(null);
      setSelectedContexts([]);

      // LA CONFERMA (D-ADMIN2, RD-153): si scrive QUI, subito dopo il `put`
      // riuscito e prima della rilettura — l'esito è quello della scrittura,
      // e un guasto della sola rilettura (catch qui sotto) non lo cambia.
      setEsitoPagina({
        tipo: "ok",
        testo: `Contexts saved for ${[selectedUser.firstName, selectedUser.lastName]
          .filter(Boolean)
          .join(" ")}.`
      });

      // ⚠️ RILETTURA CON CATCH PROPRIO. È solo una lettura: se fallisce, la
      // scrittura è comunque riuscita e l'esito resta un successo. Prima
      // condivideva il catch della scrittura, e un guasto di sola lettura
      // sarebbe finito nel ramo che scrive «the contexts were not changed» —
      // cioè il contrario del vero. Stesso rimedio, e stesso motivo, della
      // rilettura di EditMemberModal (U1-S coda/1).
      try {
        await loadUsers();

        if (selectedUser.id === user?.id) {
          await refreshUserFromBackend();
        }
      } catch (err) {
        console.error("Context list reload failed after save:", err);
      }

    } catch (err) {
      // Il `console.error` resta: è la diagnostica, e porta l'errore INTERO —
      // percorso, stato HTTP e corpo — che il messaggio a schermo riassume.
      console.error("Context save error:", err);

      // Prima di P5 il catch finiva qui, e il 403 era MUTO: `setSelectedUser(null)`
      // sta dentro il try dopo l'await, quindi la finestra restava aperta con
      // le spunte come si trovavano e non accadeva nulla. Il bottone Save
      // «appariva rotto».
      //
      // Il dettaglio del backend si AGGIUNGE alla frase generica, non la
      // sostituisce: da solo può essere la sola parola "Forbidden" (il 403 di
      // autorità sul bersaglio, server.js, non porta `detail`), che a chi
      // amministra non direbbe nulla. Così il caso con `detail` guadagna la
      // spiegazione del backend e quello senza resta comunque leggibile.
      //
      // ⚠️ I `detail` di questa rotta sono in inglese, tranne uno in
      // ITALIANO (il rifiuto del ruolo non ammesso nei contesti), e
      // questa interfaccia è in inglese: la frase mista è voluta — perdere
      // l'unica riga che spiega il rifiuto sarebbe peggio — ma è materia di
      // resa, da sanare quando si deciderà la lingua dei messaggi di servizio.
      //
      // R9.4: due rami davanti a quello di sempre — la sessione scaduta (401,
      // stesso testo di tutta l'app) e il 403 d'autorità (permesso negato, non
      // «riprova»). Ogni altro rifiuto, compreso il 403 CON `detail`, resta
      // com'era.
      if (err?.status === 401) {
        setMessaggio({ tipo: "errore", testo: SESSION_EXPIRED_MESSAGE });
        return;
      }

      if (eRifiutoDiAutorita(err)) {
        setMessaggio({ tipo: "errore", testo: AUTORITA_NEGATA });
        return;
      }

      const dettaglio = dettaglioDalBackend(err);

      setMessaggio({
        tipo: "errore",
        testo: dettaglio ? `${SALVATAGGIO_FALLITO} (${dettaglio})` : SALVATAGGIO_FALLITO
      });
    }
  };

  /* =========================
     AUTH
  ========================= */
  // Gate unico (src/auth/access.js): entra chi è ADMIN o SUPERADMIN per
  // `role` di primo livello OPPURE per una tessera ACTIVE con quel ruolo, in
  // qualunque grafia (cantiere ADMIN-MUC). Mai confronti a mano: è lo stesso
  // aggancio di `eAmministratore` qui sopra e di TeamAssignment.jsx.
  // Il recinto sulle AZIONI sta nel backend: su un bersaglio di autorità pari
  // o superiore (un ADMIN, un SUPERADMIN, sé stesso) il salvataggio è
  // rifiutato con 403 per autorità sul bersaglio; la pagina non lo anticipa.
  const isAdmin = hasRole(user, "ADMIN", "SUPERADMIN");

  // ⚠️ IL GATE NON CAMBIA (F10-P3, solo aspetto): cambia DOVE si vede il
  // rifiuto. Prima si rendeva con `.home-main-container`, classe di un
  // foglio che questa pagina non importa nemmeno — a schermo non le
  // arrivava alcuna regola, e il messaggio compariva nudo, fuori dal
  // telaio. Ora sta dentro HomeShell, in testo M sulla colonna standard
  // (doc 07 §8.1): chi non ha titolo per entrare vede comunque la barra
  // utente e il «Back» per uscire.
  if (!isAdmin) {
    return (
      <HomeShell backTo="/home">
        <h1 className="qpsc-page-title">Manage User Contexts</h1>
        <p className="ucm-denied">
          You are not authorized to view this page.
        </p>
      </HomeShell>
    );
  }

  /* =========================
     RENDER
  ========================= */
  return (
    <HomeShell backTo="/home" width="admin">
      {/* Telaio comune (doc 07 §7): barra utente, banda con «‹ Back» e
          «Switch role», colonna degli strumenti admin — a tutta finestra
          sopra i 768px (§8.1), come Team Assignment. Il titolo è la
          classe condivisa (§3.1, centrato in Rosso QPSC): non più un
          <h2> nudo, che l'eredità globale colorava per conto suo. */}
      <h1 className="qpsc-page-title">Manage User Contexts</h1>
      <p className="qpsc-note ucm-centered">
        Manage the roles (contexts) of each member
      </p>

      {/* doc 07 §8.2: la versione intera — i due anni, la fascia di fase, la
          riga delle iscrizioni. Nessuna prop: `showPhase` e `showEnrollments`
          restano ai loro default, `override` spetta a chi SCRIVE la stagione
          (Season Management) e la `note` parla di un gesto che qui non si fa.
          Sta sopra la barra dei comandi perché dichiara la cornice dentro cui
          va letto il selettore «Season Viewed»: la barra dice come sta il
          club, il selettore che cosa sto guardando io.
          ⚠️ Lo spazio verticale non si scrive: la barra è figlia diretta della
          colonna del telaio (`.qpsc-column`, HomeShell.css), che ha già il gap
          di 16px fra i blocchi. Stessa scelta di User List. */}
      <SeasonStatusBar />

      <div className="ucm-toolbar">
        {/* IL SELETTORE D'ANNATA (MUC-1 R7) — ed è una LEVA D'AZIONE, non un
            filtro di comodo: cambia insieme CHI compare nell'elenco e l'anno
            su cui il Save scrive. È la sola cosa che distingue questo campo
            dal «Season Viewed» di User List, che invece è di sola lettura;
            l'etichetta resta la stessa perché la domanda a cui risponde è la
            stessa, e due nomi per lo stesso concetto si leggerebbero come due
            concetti.
            ⚠️ Il valore viaggia come STRINGA nel DOM e come NUMERO nello
            stato: la conversione sta qui e in un punto solo.
            ⚠️ Il campo resta vuoto in un caso solo — stagione non letta — e
            allora non c'è comunque nessuna voce da offrire. */}
        <div className="qpsc-field">
          <span className="qpsc-field__label">Season Viewed</span>
          <select
            className="qpsc-select"
            aria-label="Season viewed"
            value={annoGuardato === null ? "" : String(annoGuardato)}
            onChange={(e) => setStagioneScelta(Number(e.target.value))}
          >
            {stagioniDisponibili.map((anno) => (
              <option key={anno} value={String(anno)}>{anno}</option>
            ))}
          </select>
        </div>

        <div className="qpsc-field">
          <span className="qpsc-field__label">Search</span>
          <input
            className="qpsc-input"
            aria-label="Search by name"
            placeholder="Search by name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="qpsc-field">
          <span className="qpsc-field__label">Filter by role</span>
          <select
            className="qpsc-select"
            aria-label="Filter by role"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="ALL">All Roles</option>
            <option value="Player">Player</option>
            <option value="Coach">Coach</option>
            {/* «Manager» esce anche da qui — era l'ultima voce di un ruolo morto
                offerta da questa pagina, insieme a quella delle caselle che
                assegnano un context: entrambe dichiarate nel commento di
                access.js, ed entrambe cadute in P5 — e al suo posto entra
                «Parent», che mancava pur essendo un ruolo del modello nuovo.
                ⚠️ È un FILTRO, non una scrittura: il confronto che lo applica
                (nell'effetto dei filtri, `u.role?.toLowerCase() ===
                roleFilter.toLowerCase()`) resta quello di prima, tollerante
                alla grafia ma non al vocabolario.
                Quindi un documento storico "Manager" non risponde più ad alcuna
                voce di questa tendina; resta visibile sotto «All Roles». */}
            <option value="Parent">Parent</option>
            <option value="Admin">Admin</option>
          </select>
        </div>
      </div>

      {/* LA CONFERMA DI PAGINA (D-ADMIN2, RD-153): FUORI dalla finestra, che
          al successo si chiude, e sopra l'elenco, dove chi ha salvato torna a
          guardare. Verde delle conferme (`--qpsc-green`, §1.3) e
          `role="status"`, come EditMemberModal. */}
      {esitoPagina && (
        <p
          className={`ucm-msg ucm-msg--${esitoPagina.tipo} ucm-centered`}
          role="status"
        >
          {esitoPagina.testo}
        </p>
      )}

      {/* §8.1 due rese dello STESSO contenuto: card impilate sotto i 768px,
          tabella a quattro colonne sopra. La tabella nativa è caduta: il
          contenitore è quello condiviso degli strumenti admin, che sotto i
          768px è una colonna di card e sopra una griglia.
          Le intestazioni esistono solo nella resa a tabella (il foglio
          condiviso le nasconde sotto i 768px): sulle card l'etichetta la
          porta ogni cella. */}
      <div className="qpsc-admin-list ucm-list">
        <div className="qpsc-admin-list__head" aria-hidden="true">
          <span className="qpsc-admin-list__col">Name</span>
          <span className="qpsc-admin-list__col">Registered role</span>
          <span className="qpsc-admin-list__col">Contexts</span>
          <span className="qpsc-admin-list__col" />
        </div>

        {filteredUsers.map((u) => {
          // ⚠️ Lo status col normalizzatore di sede unica (F9b), come in
          // `openEditor` e nel gate qui sopra: la colonna elenca le tessere
          // che il backend considera attive, grafia «Active» compresa. La
          // LETTURA non è cambiata di un carattere — stesso campo, stesso
          // filtro, stesso ordine.
          //
          // ⚠️ Cambia come il ruolo si SCRIVE a schermo: non più il valore
          // grezzo della tessera (`c.role`) ma la sua etichetta canonica
          // (doc 07 §8.1: «anche una tessera in grafia storica si mostra con
          // l'etichetta canonica»; §3.2: il tutto-maiuscolo sta solo nelle
          // pastiglie). Una tessera "SuperAdmin" si legge «Super Admin», una
          // "Manager" si legge «Coach». È resa, non dato: nei documenti non
          // cambia nulla, e questa pagina non scrive di qui. Chi ha bisogno
          // della grafia vera del documento la legge in Firestore, non in un
          // elenco a schermo.
          //
          // ⚠️ ANCHE LE TESSERE IN ATTESA SI VEDONO (YS-3b), col loro segno.
          // Prima la colonna mostrava le sole ACTIVE, e una tessera PENDING
          // non compariva da nessuna parte: chi amministra vedeva «Player»
          // dove la persona aveva «Player e Parent», e riapriva la finestra
          // per assegnare un ruolo che aveva già. La nota «(pending)» non è
          // un dettaglio di cortesia: dice la sola cosa che serve sapere, e
          // cioè che quel ruolo NON vale ancora — i lettori del backend
          // scartano le tessere non ACTIVE — e che ad accenderlo è l'Approve.
          // Testo semplice, nessuno stile nuovo: la cella è una sola riga.
          //
          // ⚠️ E OGNI TESSERA DICE IL SUO ANNO (MUC-1 R8). Era la cecità che
          // reggeva le altre tre: la colonna elencava «Player, Coach» senza
          // dire di quale annata fossero, e l'amministratore non aveva modo di
          // sapere su che anno stesse lavorando né di accorgersi che una delle
          // due era del rinnovo. Ora l'anno c'è, accanto al ruolo, e insieme al
          // selettore in cima chiude il cerchio: si vede l'annata che si sta
          // guardando e l'annata di ciò che si guarda.
          //
          // ⚠️ La tessera SENZA anno si dichiara «(no season)» invece di
          // tacere: è il socio non ancora migrato, e il vuoto — che a schermo
          // non si distingue da una dimenticanza — direbbe meno del vero.
          //
          // ⚠️ MUC-2 R2 — il filtro è SPEZZATO IN DUE, non cambiato. Prima
          // `filter` e `map` erano una catena sola; ora il risultato del
          // filtro ha un nome (`tessereMostrate`) perché serve DUE volte:
          // per le voci da scrivere e per la domanda «c'è un ruolo in più?».
          // Stesso campo, stesso predicato, stesso ordine: la LETTURA non è
          // cambiata di un carattere.
          const tessereMostrate = Array.isArray(u.contexts)
            ? u.contexts.filter((c) => {
                const stato = normalizeRole(c?.status);
                return stato === "ACTIVE" || stato === "PENDING";
              })
            : [];

          const tessere = tessereMostrate.map((c) => {
            const anno = annoDiLista(c?.cardSeason);
            const voce = `${etichettaRuolo(c.role)} ${
              anno === null ? "(no season)" : anno
            }`;
            return normalizeRole(c?.status) === "PENDING"
              ? `${voce} (pending)`
              : voce;
          });

          // ⚠️ MUC-2 R2 — QUANDO la colonna parla. «Context» vuol dire un
          // ruolo IN PIÙ di quello registrato: se la persona porta soltanto
          // il ruolo che la colonna accanto già dichiara, questa cella non
          // ha nulla da aggiungere e resta VUOTA. Prima diceva «No contexts
          // yet» su ogni riga senza tessere — una nota che in un elenco
          // lungo si ripeteva decine di volte senza dire nulla di nuovo.
          //
          // Il metro dei ruoli che CONTANO è `CONTEXT_TYPES` (PLAYER, COACH,
          // PARENT), la stessa costante che governa le caselle della finestra
          // e l'avviso delle altre annate (`openEditor`): le tessere
          // AMMINISTRATIVE (ADMIN, SUPERADMIN) non sono «un ruolo in più»,
          // perché non descrivono un modo di stare nel club ma un potere
          // sull'app — e infatti questa pagina non le assegna nemmeno più.
          // Il confronto con il ruolo registrato passa da `canonicalRole`,
          // come ogni altra domanda di ruolo qui dentro: è una domanda di
          // RESA (che cosa scrivere), non una scrittura.
          //
          // Esempi, a verbale: Player e basta → nulla in più → vuota;
          // Super Admin che è anche Coach → Coach è in più → la cella parla
          // (e mostra ENTRAMBE le tessere); Player che è anche Coach → due
          // righe; Player che è anche Admin → nulla in più → vuota.
          const ruoloRegistrato = canonicalRole(u.role);
          const ruoliInPiu = new Set(
            tessereMostrate
              .map((c) => canonicalRole(c?.role))
              .filter(
                (r) => CONTEXT_TYPES.includes(r) && r !== ruoloRegistrato
              )
          );

          return (
            <div className="ucm-row" key={u.id}>
              <div className="ucm-cell ucm-cell--name">
                <span className="ucm-name">
                  {u.firstName} {u.lastName}
                </span>
                {/* D-ADMIN2, RD-153: la pastiglia accanto al nome, sulla
                    stessa linea (il guscio `.ucm-cell--name` è in riga per
                    questo). Annata guardata; se è `null` (stagione non letta)
                    `categoriaAnagrafica` ripiega su oggi. Chi non ha genere o
                    data di nascita non ha pastiglia: tace, non inventa. */}
                <PastigliaCategoriaSeNota
                  category={categoriaAnagrafica(u, annoGuardato)}
                />
              </div>

              <div className="ucm-cell ucm-cell--role">
                <span className="qpsc-field__label">Registered role</span>
                <span className="ucm-value--role">
                  {etichettaRuolo(u.role)}
                </span>
              </div>

              <div className="ucm-cell ucm-cell--contexts">
                <span className="qpsc-field__label">Contexts</span>
                {/* MUC-2 R2/R3 — quando non c'è un ruolo in più la cella è
                    VUOTA: nessun testo di riempimento, nessuna nota. E
                    quando parla, ogni tessera sta sulla PROPRIA riga (R3):
                    in fila, separate da virgola, l'anno di una si leggeva
                    attaccato al ruolo della successiva. Le righe le impila
                    il CSS (`.ucm-value--contexts`, foglio §3): la riga della
                    tabella resta UNA per persona.
                    La chiave unisce voce e posizione perché due tessere
                    identiche (stesso ruolo, stesso anno, stesso stato) sono
                    dati possibili, e non devono collidere. */}
                {ruoliInPiu.size > 0 && (
                  <span className="ucm-value--contexts">
                    {tessere.map((voce, i) => (
                      <span key={`${voce}-${i}`}>{voce}</span>
                    ))}
                  </span>
                )}
              </div>

              {/* Il comando c'è su OGNI riga, in ogni stato: §5.1 oro, a
                  piena larghezza sulla card e compatto in tabella. */}
              <div className="ucm-cell ucm-cell--action">
                <button
                  className="qpsc-tool-btn qpsc-tool-btn--row"
                  onClick={() => openEditor(u)}
                >
                  Edit Contexts
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {selectedUser && (
        <div className="qpsc-modal-overlay">
          <div className="qpsc-modal">
            {/* §8 titolo XL in testo principale, mai oro pieno; separatore
                §3.3 (trattino lungo con spazi). Il guscio è quello
                condiviso, lo stesso della modale Approved Teams di Team
                Assignment: due strumenti gemelli non hanno due modali
                diverse. */}
            <h2 className="qpsc-modal__title">
              Edit contexts — {selectedUser.firstName} {selectedUser.lastName}
            </h2>

            {/* Il corpo è l'unica parte che scorre (§8: scorrimento interno
                senza barra a vista); titolo, messaggio e comandi restano
                fermi. */}
            <div className="qpsc-modal__body">
              {CONTEXT_TYPES.map((r) => {
                // ⚠️ MUC-3 R1 — LE CASELLE CHE LA MATRICE NON AMMETTE SONO
                // OSCURATE E SPENTE (`qpsc-check--disabled`, AdminTools.css §4,
                // doc 07 «stato disabilitato»: opacità 50%, cursore normale —
                // lo stesso stato che Team Assignment già usa sulle sue
                // caselle non agibili). Il comando resta l'<input> nativo, che
                // riceve `disabled`: non è solo un colore, non è cliccabile.
                //
                // ⚠️ DUE MOTIVI DIVERSI PER UNA CASELLA SPENTA, e la pagina li
                // distingue con `giaPosseduto` — cioè con la SPUNTA, che è lo
                // stato che `openEditor` ha letto dalle tessere vere:
                //   · SPENTA E VUOTA → «non aggiungibile»: la matrice non
                //     ammette questo context per il ruolo registrato di questa
                //     persona. Premere non farebbe nulla, e salvarlo sarebbe
                //     un 400 dal backend.
                //   · SPENTA E SPUNTATA → «già posseduto, da conservare»: la
                //     persona porta DAVVERO quella tessera (dato storico fuori
                //     matrice, oppure la tessera del suo stesso ruolo
                //     registrato). Lo stato esistente NON si cancella dalla
                //     grafica e NON si può togliere per errore: la casella
                //     resta spuntata, il ruolo continua a partire nel payload
                //     e il backend la CONSERVA identica, senza archiviarla.
                // È il motivo per cui `spento` guarda `ammesso` e NON
                // `ammesso || giaPosseduto`: la seconda forma avrebbe reso
                // togliibile proprio la tessera che si vuole proteggere.
                //
                // ⚠️ NESSUN TESTO NUOVO nella finestra: la ragione sta nel
                // `title` della casella, cioè nel suggerimento che compare
                // passandoci sopra — non in una riga d'avviso in più.
                //
                // ⚠️ E IL `title` NON È UN NOME ACCESSIBILE: sta sulla
                // <label>, e un lettore di schermo può benissimo non
                // annunciarlo (tanto più su un comando disabilitato, che in
                // modalità moduli spesso si salta). Ciò che la casella
                // annuncia resta la sua etichetta, più lo stato «spento».
                // Se servirà un annuncio vero sarà un `aria-describedby` in
                // una fetta di accessibilità, non un testo inventato qui.
                const giaPosseduto = selectedContexts.includes(r);
                const ammesso = contextAmmessi(selectedUser.role).includes(r);
                const spento = !ammesso;

                // ⚠️ IL RUOLO REGISTRATO SI NOMINA SOLO SE LO SI SA NOMINARE.
                // Su una grafia anomala (`role` assente, FAN, ESPORT)
                // `etichettaRuolo` restituisce il codice grezzo, o una
                // stringa vuota: la frase diventerebbe «…for a FAN member»
                // — tutto maiuscolo in prosa, che il doc 07 §3.2 riserva
                // alle pastiglie — oppure «…for a  member», con il buco e il
                // doppio spazio. Quando il ruolo non è uno dei cinque della
                // matrice, la frase non lo nomina affatto. (Il backend, che
                // parla a un log e non a schermo, dice «UNKNOWN».)
                const ruoloNoto = Object.prototype.hasOwnProperty.call(
                  CONTEXT_AMMESSI_PER_RUOLO,
                  canonicalRole(selectedUser.role)
                );

                const perche = giaPosseduto
                  ? `Existing ${etichettaRuolo(r)} context: it is kept as it ` +
                    `is and cannot be removed here.`
                  : ruoloNoto
                  ? `${etichettaRuolo(r)} is not an allowed context for a ` +
                    `${etichettaRuolo(selectedUser.role)} member.`
                  : `${etichettaRuolo(r)} is not an allowed context for ` +
                    `this member.`;

                return (
                  // §8 «Spunte»: quadrato 24px disegnato dallo <span>, non
                  // dall'<input> — che resta il comando vero, nativo e
                  // focusabile, con lo STESSO stato e lo STESSO onChange di
                  // prima. È il rimedio già adottato in Team Assignment
                  // contro le regole sui tag nudi del foglio d'eredità, che
                  // deformavano la casella (AdminTools.css §4).
                  //
                  // ⚠️ L'etichetta è quella canonica — «Player», «Coach»,
                  // «Parent» — non più il codice grezzo del vocabolario
                  // (doc 07 §3.2: il tutto-maiuscolo sta solo nelle
                  // pastiglie; scelto in chat il 24/8/2026). Cambia ciò che
                  // si stampa, non ciò che si spedisce: `CONTEXT_TYPES` è
                  // intatto e al Save parte sempre `r`, cioè "PLAYER",
                  // "COACH", "PARENT" — i soli valori che il backend ammette
                  // (server.js).
                  <label
                    key={r}
                    className={`qpsc-check${spento ? " qpsc-check--disabled" : ""}`}
                    title={spento ? perche : undefined}
                  >
                    <input
                      className="qpsc-check__input"
                      type="checkbox"
                      checked={giaPosseduto}
                      onChange={() => toggleContext(r)}
                      disabled={spento}
                    />
                    <span className="qpsc-check__box" aria-hidden="true" />
                    <span>{etichettaRuolo(r)}</span>
                  </label>
                );
              })}
            </div>

            {/* ⚠️ QUI STAVA LA CASELLA «All teams», offerta al solo COACH.
                Caduta in F3c: questa finestra dichiara i RUOLI, le squadre le
                dichiara Team Assignment (una porta sola per dato). Non è una
                funzione persa — «tutte le squadre» si dà da lì, per tessera,
                accanto all'elenco vero — ed è invece un danno tolto: la
                casella si accendeva da sola su un coach con squadre, e il
                Save lo allargava a tutte. */}

            {/* Il messaggio sta SOPRA i bottoni, cioè dove guarda chi ha
                appena premuto Save e non ha visto succedere niente. Sta
                FUORI dal corpo scorrevole di proposito: è la parte che deve
                vedersi sempre, qualunque sia la posizione dell'elenco.
                ⚠️ Lo stile inline è caduto (F10-P3): il rosso `#b30000` non
                è in palette e il grassetto non è previsto. Ora due classi
                del foglio — errore nel rosso LEGGIBILE `--qpsc-red-text`
                (§1.3: a 14px il Rosso QPSC non raggiunge AA), avviso in
                testo secondario, perché non è un guasto ma la nota che la
                finestra dà aprendosi. La logica che li produce non è
                toccata: stesso stato, stessi due tipi, stessi testi. */}
            {messaggio && (
              <p className={`ucm-msg ucm-msg--${messaggio.tipo}`}>
                {messaggio.testo}
              </p>
            )}

            {/* Stessa gerarchia della modale Approved Teams di Team
                Assignment (§5): Save è il pulsante ORO §5.1, Cancel il
                FANTASMA §5.3. */}
            <div className="qpsc-modal__actions">
              <button className="qpsc-tool-btn" onClick={saveContexts}>
                Save
              </button>
              <button
                className="qpsc-ghost-btn"
                onClick={() => setSelectedUser(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </HomeShell>
  );
}